<template>
  <div class="app-layout">
    <!-- 顶部区域：logo、导航、搜索 -->
    <div class="top-bar" :class="{ 'hidden': !showHeader }">
      <img src="@/static/logo.png" alt="子沐研究 Logo" class="logo" />
      <div class="top-nav">
        <!-- 下拉菜单的导航项 -->
        <div
          v-for="(item, index) in navItems"
          :key="index"
          class="nav-item"
          @mouseenter="handleDropdownMouseEnter(index)"
          @mouseleave="handleDropdownMouseLeave(index)"
          @click="handleDropdownClick(index)"
        >
          {{ item.title }}
          <!-- 下拉菜单（两列布局） -->
          <div 
            v-if="activeDropdownIndex === index" 
            class="dropdown-menu"
            @mouseenter="handleDropdownMenuMouseEnter()"
            @mouseleave="handleDropdownMenuMouseLeave()"
          >
            <div class="dropdown-column">
              <div
                v-for="(subItem, subIndex) in item.subItems.slice(0, Math.ceil(item.subItems.length / 2))"
                :key="subIndex"
                class="dropdown-item"
                @click.stop="handleSubItemClick(subItem.route,subItem.title,subItem)"
              >
                {{ subItem.title }}
              </div>
            </div>
            <div class="dropdown-column">
              <div
                v-for="(subItem, subIndex) in item.subItems.slice(Math.ceil(item.subItems.length / 2))"
                :key="subIndex"
                class="dropdown-item"
                @click.stop="handleSubItemClick(subItem.route, subItem.title, subItem)" 
              >
                {{ subItem.title }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="search-box">
        <input 
          type="text" 
          placeholder="输入搜索内容" 
          v-model="searchKeyword"
          @keyup.enter="handleSearch"
        />
        <span class="search-icon" @click="handleSearch">🔍</span>
      </div>
      
      <!-- 右上角：用户信息 -->
      <div class="top-right">
        <div class="user-info" v-if="userName">
          <span class="user-name">{{ userName }}</span>
        </div>
      </div>
    </div>

    <!-- 页签栏 -->
    <div class="tabs-bar" :class="{ 'hidden': !showTabs }">
      <div v-for="tab in tabs" :key="tab.key" class="tab-item" :class="{ 'active': tab.active }" @click="handleTabClick(tab.key)">
        {{ tab.title }}
        <span class="close-icon" v-if="tab.key !== 'home'" @click.stop="handleTabClose(tab.key)">
          ×
        </span>
      </div>
    </div>

    <!-- 主体内容区域：侧边栏 + 路由视图 -->
    <div class="main-container">
      <!-- 侧边栏-->
      <AppSidebar @open-tab="openNewTab" v-if="currentTabKey === 'home'"/>
      <!-- 路由视图：展示对应页面内容 -->
      <router-view class="main-content"/>
    </div>

    <!-- 聊天组件 -->
    <div class="consult-btn" @click="toggleChat">
      <div class="consult-btn-inner">
        <i class="icon-comment"></i>
        <span>咨询</span>
      </div>
    </div>

    <!-- 聊天容器 -->
    <div 
      class="chat-container" 
      :class="{ 'chat-visible': isChatVisible }"
    >
      <ChatComponent 
        v-if="isChatVisible" 
        :key="isChatVisible ? 'chat-active' : 'chat-inactive'"
        :is-window-active="isChatVisible" 
        @close="toggleChat"
      />
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import AppSidebar from '@/components/appSidebar.vue';
import ChatComponent from '@/components/chatComponent.vue';  

export default {
  components: { AppSidebar, ChatComponent },
  setup() {    
    const router = useRouter();
    const userStore = useUserStore();

    // 页签数据，初始包含首页
    const tabs = reactive([
      { title: '首页', key: 'home', active: true },
    ]);
    const currentTabKey = ref('home');
    // 控制头部和侧边栏显示
    const showHeader = ref(true);
    const showTabs = ref(true);

    const activeDropdownIndex = ref(-1); // -1 表示无激活下拉菜单
    const dropdownTimer = ref(null); // 延迟隐藏定时器
    const searchKeyword = ref(''); // 搜索关键词

    // 获取用户名
    const userName = computed(() => userStore.getUserName);

    // 清除定时器（避免内存泄漏）
    onUnmounted(() => {
      clearTimeout(dropdownTimer.value);
    });

    // 导航项鼠标进入
    const handleDropdownMouseEnter = (index) => {
      clearTimeout(dropdownTimer.value);
      activeDropdownIndex.value = index;
    };

    // 导航项鼠标离开
    const handleDropdownMouseLeave = (index) => {
      dropdownTimer.value = setTimeout(() => {
        if (activeDropdownIndex.value === index) {
          activeDropdownIndex.value = -1;
        }
      }, 200);
    };

    // 下拉菜单鼠标进入
    const handleDropdownMenuMouseEnter = () => {
      clearTimeout(dropdownTimer.value);
    };

    // 下拉菜单鼠标离开
    const handleDropdownMenuMouseLeave = () => {
      dropdownTimer.value = setTimeout(() => {
        activeDropdownIndex.value = -1;
      }, 100);
    };

    // 导航项点击
    const handleDropdownClick = (index) => {
      if (activeDropdownIndex.value === index) {
        activeDropdownIndex.value = -1;
      } else {
        clearTimeout(dropdownTimer.value);
        activeDropdownIndex.value = index;
      }
    };    

    // 导航菜单数据
    const navItems = reactive([
      {
        title: "我的功能",
        subItems: [
          { title: "我的账户", route: "myAccount" },
          //{ title: "自选股", route: "favorites" },
        ],
      },
      {
        title: "北交所",
        subItems: [
          { title: "核心池", route: "corePool",params: { comp_exch: "BJ" }},
          { title: "Top50", route: "top50PortfolioDetail", query: { group_type: 50, index_type: "ZM" } },
          { title: "调研报告", route: "investigationReport", params: { comp_exch: "BJ" }},
          { title: "并购重组", route: "mergersAndAcquisitions" },
          //{ title: "融资", route: "financing" },
          { title: "大宗交易", route: "blockTrades", params: { comp_exch: "BJ" } },
          { title: "龙虎榜", route: "dragonAndTigerList" },
          { title: "标签管理", route: "sectorDataTable" },
          { title: "行情统计", route: "marketPerformance", params: { comp_exch: "BJ" }},
        ],
      },
      {
        title: "IPO",
        subItems: [
          { title: "潜力公司", route: "highPotentialCompany" },
          { title: "上会过会", route: "ipoReviewMeeting"},
          { title: "筹备期", route: "ipoStateList",params:{ state_stage: '筹备期'}},
          { title: "辅导期", route: "ipoStateList",params:{ state_stage: '辅导期'} },
          { title: "审核期", route: "ipoStateList",params:{ state_stage: '审核期'}},
          { title: "审议期", route: "ipoStateList",params:{ state_stage: '审议期'} },
          { title: "注册期", route: "ipoStateList",params:{ state_stage: '注册期'} },
          { title: "发行方案", route: "ipoIssuePlan"},
          { title: "终止原因", route: "ipoTermination" },
          { title: "问询问题", route: "ipoInquiry" },
          { title: "参会委员", route: "committeeMembers" },
        ],
      },
      {
        title: "新三板",
        subItems: [
          { title: "挂牌公司", route: "newlyListed" },
          { title: "定向增发", route: "privatePlacement" },
          { title: "壳交易", route: "shellTransactions" },
          { title: "大宗交易", route: "blockTrades",params: { comp_exch: "NQ" }},
          { title: "研究报告", route: "investigationReport", params: { comp_exch: "NQ" }},
          //{ title: "新闻资讯", route: "news_new" },
          { title: "机构名录", route: "institutionDirectory" },
          { title: "行情统计", route: "marketPerformance", params: {comp_exch: "NQ" }},
          { title: "做市商", route: "marketMakerChange" },
          { title: "股票池", route: "stocksGroup",params: { comp_exch: "NQ" }},
        ],
      },
      {
        title: "一级市场",
        subItems: [
          { title: "项目库", route: "projectDirectory" },
          { title: "立项库", route: "projectFoundList" },
          { title: "投前", route: "preInvestment" },
          { title: "投中", route: "duringInvestment" },
          { title: "投后", route: "postInvestment" },
        ],
      }
    ]);

    // 监听当前页签变化
    watch(currentTabKey, ()=> {
      showHeader.value = true;
      showTabs.value = true;
    });

    // 处理页签点击
    const handleTabClick = (key) => {
      tabs.forEach(tab => tab.active = tab.key === key);
      currentTabKey.value = key;
      
      const targetTab = tabs.find(tab => tab.key === key);
      if (targetTab) {
        // 从页签数据中获取原始路由名（拆分uniqueKey）
        const originalRouteName = key.split('-')[0];
        router.push({ 
          name: originalRouteName, 
          params: targetTab.params || {},
          query: targetTab.query || {}
        });
      }
    };

    // 处理页签关闭
    const handleTabClose = (key) => {
      const index = tabs.findIndex(tab => tab.key === key);
      if (index !== -1) {
        const isActive = key === currentTabKey.value;
        tabs.splice(index, 1);
        
        if (isActive && tabs.length > 0) {
          let newIndex = index;
          if (newIndex >= tabs.length) {
            newIndex = tabs.length - 1;
          }
          handleTabClick(tabs[newIndex].key);
        } else if (tabs.length === 0) {
          tabs.push({ title: '首页', key: 'home', active: true });
          handleTabClick('home');
        }
      }
    };

    const openNewTab = (title, key, params = {}, query = {}) => {
      // 深拷贝参数确保数据纯净性
      const paramsCopy = JSON.parse(JSON.stringify(params));
      const queryCopy = JSON.parse(JSON.stringify(query));
      
      // 规范化函数：将所有参数值转为字符串，确保与路由返回的 to.query/to.params 格式一致
      const normalize = (obj) => {
        return Object.keys(obj).sort().reduce((acc, k) => {
          if (obj[k] !== undefined && obj[k] !== null) {
            acc[k] = String(obj[k]);
          }
          return acc;
        }, {});
      };

      const sortedParams = normalize(paramsCopy);
      const sortedQuery = normalize(queryCopy);
      const paramStr = JSON.stringify({ ...sortedParams, ...sortedQuery });
      const uniqueKey = `${key}-${paramStr}`; // 路由名+参数作为唯一标识

      // 查找已有页签
      const existingTab = tabs.find(tab => tab.key === uniqueKey);
      
      if (existingTab) {
        // 已存在则更新标题并切换
        existingTab.title = title;
        existingTab.params = sortedParams;
        existingTab.query = sortedQuery;
        handleTabClick(uniqueKey);
      } else {
        // 新页签逻辑
        tabs.forEach(tab => tab.active = false);
        tabs.push({ 
          title: title, 
          key: uniqueKey, 
          active: true, 
          params: sortedParams,
          query: sortedQuery
        });
        currentTabKey.value = uniqueKey;
        router.push({ name: key, params: sortedParams, query: sortedQuery });
      }
    };

    // 子菜单点击事件
    const handleSubItemClick = (routeName, title, subItem) => {
      // 传递子项的完整参数（支持params和query）
      openNewTab(
        title, 
        routeName, 
        subItem?.params || {}, 
        subItem?.query || {}
      );
      activeDropdownIndex.value = -1;
    };

    // 路由切换时更新页签状态
    router.afterEach((to) => {
      // 规范化函数：确保与 openNewTab 中的 key 生成逻辑完全一致
      const normalize = (obj) => {
        return Object.keys(obj).sort().reduce((acc, k) => {
          if (obj[k] !== undefined && obj[k] !== null) {
            acc[k] = String(obj[k]);
          }
          return acc;
        }, {});
      };

      const sortedParams = normalize(to.params);
      const sortedQuery = normalize(to.query);
      const paramStr = JSON.stringify({ ...sortedParams, ...sortedQuery });
      const currentRouteKey = `${to.name}-${paramStr}`;

      // 查找对应的页签
      const currentTab = tabs.find(tab => tab.key === currentRouteKey);
      if (currentTab) {
        // 更新已有页签状态
        tabs.forEach(tab => tab.active = false);
        currentTab.active = true;
        currentTab.params = to.params;
        currentTab.query = to.query;
        currentTabKey.value = currentRouteKey;
      } else {
        // 特殊处理top50页面:如果只是添加了group_id,更新现有页签而不创建新页签
        if (to.name === 'top50PortfolioDetail' && to.query.group_id) {
          // 查找没有group_id的top50页签
          const queryWithoutGroupId = { ...sortedQuery };
          delete queryWithoutGroupId.group_id;
          const paramStrWithoutGroupId = JSON.stringify({ ...sortedParams, ...queryWithoutGroupId });
          const keyWithoutGroupId = `${to.name}-${paramStrWithoutGroupId}`;
          
          const existingTop50Tab = tabs.find(tab => tab.key === keyWithoutGroupId);
          if (existingTop50Tab) {
            // 更新现有页签的key和query,而不是创建新页签
            tabs.forEach(tab => tab.active = false);
            existingTop50Tab.key = currentRouteKey;
            existingTop50Tab.query = sortedQuery;
            existingTop50Tab.active = true;
            currentTabKey.value = currentRouteKey;
            return;
          }
        }
        
        // 新增未在页签中的路由（首页除外）
        if (to.name !== 'home' && to.name) {
          tabs.forEach(tab => tab.active = false);
          tabs.push({
            title: to.meta.title || to.name,
            key: currentRouteKey,
            active: true,
            params: to.params,
            query: to.query
          });
          currentTabKey.value = currentRouteKey;
        }
      }
    });

    // 聊天窗口控制
    const isChatVisible = ref(false);
    const toggleChat = () => {
      isChatVisible.value = !isChatVisible.value;
    };

    // 搜索功能
    const handleSearch = () => {
      if (!searchKeyword.value.trim()) {
        return;
      }
      // 跳转到搜索结果页面
      router.push({
        name: 'globalSearch',
        query: { keyword: searchKeyword.value.trim() }
      });
    };

    return {
      tabs,
      currentTabKey,
      handleTabClick,
      handleTabClose,
      openNewTab,
      activeDropdownIndex,
      dropdownTimer,
      showHeader,
      showTabs,
      navItems,
      handleSubItemClick,
      isChatVisible,
      toggleChat,
      handleDropdownMouseEnter,
      handleDropdownMouseLeave,
      handleDropdownMenuMouseEnter,
      handleDropdownMenuMouseLeave,
      handleDropdownClick,
      searchKeyword,
      handleSearch,
      userName
    };
  },
};
</script>

<style scoped>
/* 隐藏类 */
.hidden {
  display: none !important;
}

.app-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.top-bar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

.logo {
  height: 40px;
  width: 160px;
  cursor: pointer;
}

.top-nav {
  display: flex;
  align-items: center;
  margin-left: 1px;
}

.nav-item {
  position: relative;
  margin-right: 24px;
  cursor: pointer;
  font-size: 15px;
  color: #333;
  transition: color 0.2s;
  padding: 12px 0;
}

.nav-item:hover {
  color: #007bff;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0; 
  padding-top: 5px;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  z-index: 9999;
  display: flex;
  padding: 10px 0;
}

.dropdown-column {
  flex: 1;
  padding: 0 10px;
}

.dropdown-item {
  padding: 6px 15px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: all 0.2s;
  border-radius: 4px;
  margin: 2px 0;
}

.dropdown-item:hover {
  background-color: #f5f7fa;
  color: #007bff;
}

.search-box {
  display: flex;
  align-items: center;
  margin: 0 20px;
  flex: 0 0 300px;
  position: relative;
}

.search-box input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  font-size: 14px;
  outline: none;
  height: 36px;
  box-sizing: border-box;
  padding-right: 36px;
}

.search-box input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-icon {
  position: absolute;
  right: 12px;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.2s;
}

.search-icon:hover {
  color: #007bff;
}

.tabs-bar {
  display: flex;
  align-items: center;
  padding: 5px 10px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  overflow-x: auto;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  z-index: 99;
}

.tab-item {
  padding: 8px 15px;
  margin-right: 5px;
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  white-space: nowrap;
}

.tab-item.active {
  background-color: #f0f5ff; /* 更明显的激活背景色 */
  color: #1890ff; /* 激活文字色 */
  border-top: 2px solid #1890ff; /* 顶部高亮边框 */
  border-left: 1px solid #e8e8e8;
  border-right: 1px solid #e8e8e8;
  border-bottom: 1px solid #fff; /* 覆盖底部边框 */
}

.close-icon {
  margin-left: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #999;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-icon:hover {
  color: #ff4d4f;
  background-color: #f5f5f5;
}

/* 右上角区域样式 */
.top-right {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-name {
  color: #333;
  font-size: 14px;
}

.main-container {
  display: flex;
  flex: 1;
  margin-top: 100px;
}

.main-content {
  flex: 1;
  padding: 20px;
  background-color: #fff;
  width: 100%;
}

:deep(.main-container:not(:has(.sidebar))) .main-content {
  width: 100%;
  margin-left: 0;
}

/* 聊天相关样式 */
.consult-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  cursor: pointer;
}

.consult-btn-inner {
  writing-mode: horizontal-tb;
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.consult-btn-inner:hover {
  background-color: #0056b3;
}

.chat-container {
  width: 400px;
  background-color: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: 80vh;
  position: fixed;
  right: -400px;
  bottom: 0;
  transition: right 0.3s ease-in-out;
  z-index: 10000;
  border-radius: 8px 8px 0 0;
}

.chat-visible {
  right: 0;
}

.icon-comment::before {
  content: "💬";
}

.top-nav {
  display: flex;
  align-items: center;
  margin-left: 1px;
  /* 新增样式 */
  white-space: nowrap; /* 防止子元素换行 */
  min-width: 500px; /* 根据导航项总宽度设置合适的最小值 */
  overflow: visible; /* 保持内容可见 */
}

/* 可根据需要调整导航项间距 */
.nav-item {
  position: relative;
  margin-right: 20px; /* 适当减小间距，可根据实际情况调整 */
  cursor: pointer;
  font-size: 15px;
  color: #333;
  transition: color 0.2s;
  padding: 12px 0;
}
</style>
